import React, { useState } from "react";
import mediaUrl from "../../utils/mediaUrl";
import "./Matchup.css";

const initialsOf = (name = "") => {
  const words = String(name).replace(/[^\p{L}\p{N}\s]/gu, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

// A steady colour per name, so a team without a logo still looks like itself
const hueOf = (name = "") => {
  let hash = 0;
  for (const ch of String(name)) hash = (hash * 31 + ch.codePointAt(0)) % 360;
  return hash;
};

/**
 * A team's logo or a player's photo in a circle; their initials when there is
 * none (or it fails to load).
 */
export const ParticipantAvatar = ({ name, image, size = "md", color, kind, className = "" }) => {
  const [broken, setBroken] = useState(false);
  const src = broken ? null : mediaUrl(image);
  const style = src ? undefined : { background: color || `hsl(${hueOf(name)} 55% 42%)` };
  return (
    <span
      className={`participant-avatar participant-avatar--${size}${kind === "team" ? " participant-avatar--team" : ""} ${className}`.trim()}
      style={style}
      aria-hidden="true"
    >
      {src ? (
        <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} />
      ) : (
        // Drawn from CSS so the initials never repeat the name in the page text
        <span className="participant-avatar__initials" data-initials={initialsOf(name)} />
      )}
    </span>
  );
};

const Side = ({ side, size, align }) => (
  <div className={`matchup__side matchup__side--${align}${side.winner ? " matchup__side--winner" : ""}`}>
    <ParticipantAvatar name={side.name} image={side.image} color={side.color} kind={side.kind} size={size} />
    <span className="matchup__name" title={side.name}>
      {side.name || "TBD"}
      {side.winner && <span className="matchup__crown" aria-label="winner"> 👑</span>}
    </span>
    {side.score !== undefined && side.score !== null && <span className="matchup__score">{side.score}</span>}
  </div>
);

/**
 * Two sides of a match, sports-broadcast style: logo (or photo) on top, name
 * below, VS in the middle. sides: [{ name, image, score?, winner?, color? }].
 * size: "sm" for lists, "md" for cards, "lg" for a match page header.
 */
const Matchup = ({ home, away, size = "md", center, className = "", testId = "matchup" }) => (
  <div
    className={`matchup matchup--${size} ${className}`.trim()}
    data-testid={testId}
    aria-label={`${home?.name || "TBD"} versus ${away?.name || "TBD"}`}
  >
    <Side side={home || {}} size={size} align="home" />
    <div className="matchup__center">{center ?? <span className="matchup__vs">VS</span>}</div>
    <Side side={away || {}} size={size} align="away" />
  </div>
);

/** The two sides of a match object as the API returns it (both casings). */
export const matchSides = (match = {}) => {
  const config = (() => {
    const cfg = match.matchConfig || match.match_config;
    if (typeof cfg !== "string") return cfg || {};
    try { return JSON.parse(cfg) || {}; } catch { return {}; }
  })();
  const dropIn = config.drop_in_teams;
  return [1, 2].map((n) => {
    const key = n === 1 ? "team_a" : "team_b";
    return {
      id: match[`participant${n}Id`] ?? match[`participant_${n}_id`],
      kind: dropIn ? "team" : match[`participant${n}Type`] ?? match[`participant_${n}_type`],
      name: dropIn?.[key]?.name || match[`participant${n}Name`] || match[`participant_${n}_name`] || "TBD",
      image: match[`participant${n}Image`] ?? match[`participant_${n}_image`] ?? null,
      color: dropIn?.[key]?.color,
    };
  });
};

export default Matchup;
